/**
 * MODULE: domain/sales-profile/frontend/hooks/useProducts.ts
 * PURPOSE: React hook for product catalog management
 * 
 * DEPENDENCIES:
 *   - @tanstack/react-query: Data fetching
 *   - ../api/client: API client
 * 
 * See: /domain/sales-profile/README.md
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { salesProfileApi } from '../api/client';
import type { ProductCreate, ProductUpdate } from '../../contracts/sales-profile.types';

const QUERY_KEY = ['sales-profile', 'products'];

export function useProducts(includeInactive = false) {
  const queryClient = useQueryClient();

  const {
    data,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: [...QUERY_KEY, { includeInactive }],
    queryFn: () => salesProfileApi.listProducts(includeInactive),
  });

  const createMutation = useMutation({
    mutationFn: (data: ProductCreate) => salesProfileApi.createProduct(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ productId, data }: { productId: string; data: ProductUpdate }) =>
      salesProfileApi.updateProduct(productId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (productId: string) => salesProfileApi.deleteProduct(productId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });

  return {
    products: (data?.products ?? []).map((product) => ({
      ...product,
      features: product.features ?? [],
      benefits: product.benefits ?? [],
      differentiators: product.differentiators ?? [],
    })),
    total: data?.total ?? 0,
    isLoading,
    error: error as Error | null,
    refetch,
    
    createProduct: createMutation.mutateAsync,
    isCreating: createMutation.isPending,
    createError: createMutation.error as Error | null,
    
    updateProduct: updateMutation.mutateAsync,
    isUpdating: updateMutation.isPending,
    updateError: updateMutation.error as Error | null,
    
    deleteProduct: deleteMutation.mutateAsync,
    isDeleting: deleteMutation.isPending,
    deleteError: deleteMutation.error as Error | null,
  };
}

export function useProduct(productId: string | undefined) {
  const { data, isLoading, error } = useQuery({
    queryKey: [...QUERY_KEY, productId],
    queryFn: () => salesProfileApi.getProduct(productId!),
    enabled: !!productId,
  });

  return {
    product: data
      ? {
          ...data,
          features: data.features ?? [],
          benefits: data.benefits ?? [],
          differentiators: data.differentiators ?? [],
        }
      : undefined,
    isLoading,
    error: error as Error | null,
  };
}

export default useProducts;
